import React, { useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Keyboard, Delete, CornerDownLeft, Space } from 'lucide-react';

/**
 * Virtual On-Screen Keyboard
 * Protects against keyloggers by allowing mouse-based input
 */

const KEYBOARD_LAYOUTS = {
  default: [
    ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '-', '='],
    ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p', '[', ']'],
    ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', ';', "'"],
    ['z', 'x', 'c', 'v', 'b', 'n', 'm', ',', '.', '/'],
  ],
  shift: [
    ['!', '@', '#', '$', '%', '^', '&', '*', '(', ')', '_', '+'],
    ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P', '{', '}'],
    ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', ':', '"'],
    ['Z', 'X', 'C', 'V', 'B', 'N', 'M', '<', '>', '?'],
  ],
};

export const VirtualKeyboard = ({ onInput, onClose, isOpen }) => {
  const [value, setValue] = useState('');
  const [isShift, setIsShift] = useState(false);
  const [isCaps, setIsCaps] = useState(false);

  const layout = (isShift || isCaps) ? KEYBOARD_LAYOUTS.shift : KEYBOARD_LAYOUTS.default;

  const handleKeyPress = useCallback((key) => {
    setValue(prev => prev + key);
    if (isShift && !isCaps) {
      setIsShift(false);
    }
  }, [isShift, isCaps]);

  const handleBackspace = useCallback(() => {
    setValue(prev => prev.slice(0, -1));
  }, []);

  const handleSpace = useCallback(() => {
    setValue(prev => prev + ' ');
  }, []);

  const handleEnter = useCallback(() => {
    onInput(value);
    setValue('');
    onClose();
  }, [value, onInput, onClose]);

  const handleClear = useCallback(() => {
    setValue('');
  }, []);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Keyboard className="w-5 h-5 text-primary" />
            Secure Input (Anti-Keylogger)
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Input Display */}
          <div className="relative">
            <div className="w-full p-4 rounded-lg bg-secondary/50 border border-border min-h-[60px] font-mono text-lg text-white break-all">
              {value || <span className="text-slate-500">Click keys to type securely...</span>}
              <span className="animate-pulse">|</span>
            </div>
            <div className="absolute top-2 right-2 flex gap-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleClear}
                className="text-xs"
              >
                Clear
              </Button>
            </div>
          </div>

          {/* Security Notice */}
          <div className="flex items-center gap-2 text-xs text-emerald-400 bg-emerald-500/10 px-3 py-2 rounded-lg">
            <Keyboard className="w-4 h-4" />
            <span>Mouse-based input protects against keyboard loggers</span>
          </div>

          {/* Keyboard */}
          <div className="space-y-2 p-4 rounded-xl bg-secondary/30 border border-border">
            {layout.map((row, rowIndex) => (
              <div key={rowIndex} className="flex justify-center gap-1">
                {row.map((key) => (
                  <button
                    key={key}
                    onClick={() => handleKeyPress(key)}
                    className="w-10 h-10 rounded-lg bg-secondary hover:bg-primary/20 hover:border-primary/50 border border-border text-white font-medium transition-all duration-150 active:scale-95"
                    data-testid={`vk-key-${key}`}
                  >
                    {key}
                  </button>
                ))}
              </div>
            ))}

            {/* Bottom Row */}
            <div className="flex justify-center gap-1 mt-2">
              <button
                onClick={() => setIsShift(!isShift)}
                className={`w-20 h-10 rounded-lg border text-white font-medium transition-all duration-150 active:scale-95 ${
                  isShift ? 'bg-primary/30 border-primary' : 'bg-secondary hover:bg-primary/20 border-border'
                }`}
                data-testid="vk-shift"
              >
                Shift
              </button>
              <button
                onClick={() => setIsCaps(!isCaps)}
                className={`w-16 h-10 rounded-lg border text-white font-medium transition-all duration-150 active:scale-95 ${
                  isCaps ? 'bg-primary/30 border-primary' : 'bg-secondary hover:bg-primary/20 border-border'
                }`}
                data-testid="vk-caps"
              >
                Caps
              </button>
              <button
                onClick={handleSpace}
                className="flex-1 max-w-[200px] h-10 rounded-lg bg-secondary hover:bg-primary/20 border border-border text-white font-medium transition-all duration-150 active:scale-95 flex items-center justify-center gap-2"
                data-testid="vk-space"
              >
                <Space className="w-4 h-4" />
                Space
              </button>
              <button
                onClick={handleBackspace}
                className="w-20 h-10 rounded-lg bg-secondary hover:bg-red-500/20 hover:border-red-500/50 border border-border text-white font-medium transition-all duration-150 active:scale-95 flex items-center justify-center"
                data-testid="vk-backspace"
              >
                <Delete className="w-4 h-4" />
              </button>
              <button
                onClick={handleEnter}
                className="w-20 h-10 rounded-lg bg-primary hover:bg-primary/80 text-primary-foreground font-medium transition-all duration-150 active:scale-95 flex items-center justify-center gap-1"
                data-testid="vk-enter"
              >
                <CornerDownLeft className="w-4 h-4" />
                Done
              </button>
            </div>
          </div>

          {/* Character Count */}
          <div className="text-xs text-slate-500 text-right">
            {value.length} characters
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Mini keyboard trigger button
export const VirtualKeyboardTrigger = ({ onClick }) => {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={onClick}
      className="text-slate-400 hover:text-primary"
      title="Open secure keyboard (anti-keylogger)"
      data-testid="virtual-keyboard-trigger"
    >
      <Keyboard className="w-4 h-4" />
    </Button>
  );
};
